// Sprint 2 — Reorganización de configuración
import { useCallback, useEffect, useState } from "react";
import { css } from "styled-system/css";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { SettingsCustomFormsBlock } from "@/components/dashboard/settings-custom-forms-block";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useToast } from "@/hooks/useToast";
import type { FormsConfig } from "@/pages/dashboard/odontogram/formsConfig";

export function DashboardSettingsCustomFields() {
  const { refresh } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<FormsConfig>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await fetchWithSessionSync("/api/dashboard/me", { credentials: "include" }, refresh);
    if (r.ok) {
      const d = (await r.json()) as { professional?: { formsConfig?: FormsConfig | null } };
      setConfig(d.professional?.formsConfig ?? {});
    }
  }, [refresh]);

  useEffect(() => {
    void load().finally(() => setLoading(false));
  }, [load]);

  async function save(next: FormsConfig) {
    setConfig(next);
    setSaving(true);
    const r = await fetchWithSessionSync(
      "/api/dashboard/me",
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formsConfig: next }),
      },
      refresh,
    );
    setSaving(false);
    if (!r.ok) {
      toast.error("No se pudo guardar el cambio.");
      void load();
      return;
    }
    toast.success("Guardado");
  }

  return (
    <DashboardPageShell
      eyebrow="Configuración"
      title="Campos personalizados"
      description="Personalizá las preguntas de Antecedentes médicos y Nueva consulta del módulo Odontograma."
    >
      {loading ? (
        <div className={css({ p: "10" })}>
          <LoadingFrame variant="light" minHeight={200} />
        </div>
      ) : (
        <div className={css({ opacity: saving ? 0.75 : 1, transition: "opacity 160ms ease" })}>
          <SettingsCustomFormsBlock value={config} onChange={(next) => void save(next)} />
        </div>
      )}
    </DashboardPageShell>
  );
}
